import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import { Select } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import {
  addFieldPassTargets,
  listFieldPassAssignmentsFromFirestore,
  listFieldPassesFromFirestore,
  removeFieldPassTarget,
} from '../../services/fieldPassService'
import type { FieldPassAssignment, FieldPassRecord } from '../../types/fieldPass'
import { QuickDelegateSheet } from './QuickDelegateSheet'

type ChecklistFieldPassControlProps = {
  workspaceId: string
  checklistItemId: string
  checklistTitle: string
  canManage: boolean
  compact?: boolean
  deferInitialLoad?: boolean
  onNotice?: (message: string, tone?: 'success' | 'error') => void
}

const ACTIVE_PASS_STATUSES = new Set(['ACTIVE', 'REDEEMED'])
const ACTIVE_ASSIGNMENT_STATUSES = new Set(['ACTIVE'])

const toMs = (value: unknown): number | null => {
  if (!value || typeof value !== 'object' || typeof (value as { toMillis?: unknown }).toMillis !== 'function') return null
  return (value as { toMillis: () => number }).toMillis()
}

const formatExpiry = (value: unknown): string => {
  const ms = toMs(value)
  if (!ms) return 'expiry not set'
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

const isUsablePass = (pass: FieldPassRecord) => {
  if (!ACTIVE_PASS_STATUSES.has(pass.status)) return false
  const expiresAt = toMs(pass.expiresAt)
  return !expiresAt || expiresAt > Date.now()
}

const passLabel = (pass: FieldPassRecord): string => {
  const name = pass.displayName?.trim() || 'Unredeemed'
  const count = pass.targetIds.length
  return `${name} · ${count}/8 item${count === 1 ? '' : 's'}`
}

export const ChecklistFieldPassControl = ({
  workspaceId,
  checklistItemId,
  checklistTitle,
  canManage,
  compact = false,
  deferInitialLoad = false,
  onNotice,
}: ChecklistFieldPassControlProps) => {
  const [passes, setPasses] = useState<FieldPassRecord[]>([])
  const [assignments, setAssignments] = useState<FieldPassAssignment[]>([])
  const [selectedPassId, setSelectedPassId] = useState('')
  const [delegateOpen, setDelegateOpen] = useState(false)
  const [lastLink, setLastLink] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [recordsUnavailable, setRecordsUnavailable] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  // Keep the caller's onNotice in a ref so re-renders that pass a new inline
  // callback don't rebuild `emitNotice`, which would in turn re-run the load
  // effect below and — if the backend keeps failing (e.g. permission denied
  // returned as INTERNAL from the callable) — spam the toast queue on every
  // re-render.
  const onNoticeRef = useRef(onNotice)
  useEffect(() => { onNoticeRef.current = onNotice }, [onNotice])

  const emitNotice = useCallback((text: string, tone: 'success' | 'error' = 'success') => {
    setNotice({ tone, text })
    onNoticeRef.current?.(text, tone)
  }, [])

  const refresh = useCallback(async () => {
    if (!workspaceId || !checklistItemId || !canManage) return
    try {
      const [passRows, assignmentRows] = await Promise.all([
        listFieldPassesFromFirestore(workspaceId),
        listFieldPassAssignmentsFromFirestore(workspaceId, [checklistItemId]),
      ])
      setPasses(passRows)
      setAssignments(assignmentRows)
      setRecordsUnavailable(false)
    } catch (error) {
      console.warn('[ChecklistFieldPassControl] read failed', error)
      setRecordsUnavailable(true)
    }
  }, [canManage, checklistItemId, workspaceId])

  useEffect(() => {
    if (deferInitialLoad) return
    void refresh()
  }, [deferInitialLoad, refresh])

  const activeAssignmentCards = useMemo(() => {
    const fromAssignments = assignments
      .filter((assignment) => ACTIVE_ASSIGNMENT_STATUSES.has(assignment.status))
      .filter((assignment) => {
        const expiresAt = toMs(assignment.expiresAt)
        return !expiresAt || expiresAt > Date.now()
      })
      .map((assignment) => ({
        id: assignment.id,
        fieldPassId: assignment.fieldPassId,
        displayName: assignment.displayName || 'Unredeemed Field Pass',
        expiresAt: assignment.expiresAt,
      }))
    const seen = new Set(fromAssignments.map((assignment) => assignment.fieldPassId))
    const fromPassScope = passes
      .filter(isUsablePass)
      .filter((pass) => pass.targetIds.includes(checklistItemId) && !seen.has(pass.id))
      .map((pass) => ({
        id: `pass:${pass.id}`,
        fieldPassId: pass.id,
        displayName: pass.displayName || 'Unredeemed Field Pass',
        expiresAt: pass.expiresAt,
      }))
    return [...fromAssignments, ...fromPassScope]
  }, [assignments, checklistItemId, passes])

  const activePasses = useMemo(() => passes
    .filter(isUsablePass)
    .filter((pass) => !pass.targetIds.includes(checklistItemId) && pass.targetIds.length < 8), [checklistItemId, passes])

  const hasActiveAssignment = useMemo(() => activeAssignmentCards
    .some((assignment) => {
      const expiresAt = toMs(assignment.expiresAt)
      return !expiresAt || expiresAt > Date.now()
    }), [activeAssignmentCards])

  useEffect(() => {
    if (selectedPassId && !activePasses.some((pass) => pass.id === selectedPassId)) setSelectedPassId('')
  }, [activePasses, selectedPassId])

  const copyText = async (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(value)
  }

  const addToExisting = async () => {
    if (!selectedPassId) {
      emitNotice('Choose an active Field Pass first.', 'error')
      return
    }
    setBusy('add')
    try {
      const pass = await addFieldPassTargets(selectedPassId, [checklistItemId])
      setPasses((current) => current.map((entry) => (entry.id === pass.id ? pass : entry)))
      setRecordsUnavailable(false)
      emitNotice('Checklist item added to Field Pass.')
      await refresh()
    } catch (error) {
      emitNotice(error instanceof Error ? error.message : 'Could not add this item to the Field Pass.', 'error')
    } finally {
      setBusy(null)
    }
  }

  const removeAssignment = async (fieldPassId: string) => {
    setBusy(`remove:${fieldPassId}`)
    try {
      const pass = await removeFieldPassTarget(fieldPassId, checklistItemId)
      setPasses((current) => current.map((entry) => (entry.id === pass.id ? pass : entry)))
      setAssignments((current) => current.filter((assignment) => assignment.fieldPassId !== fieldPassId))
      emitNotice('Checklist item removed from Field Pass.')
      await refresh()
    } catch (error) {
      emitNotice(error instanceof Error ? error.message : 'Could not remove this item from the Field Pass.', 'error')
    } finally {
      setBusy(null)
    }
  }

  if (!canManage) {
    return null
  }

  return (
    <section className={compact ? 'grid gap-3' : 'rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
            <LocalOfferRoundedIcon fontSize="small" />
            Field Pass
          </div>
          <p className="mt-1 text-xs text-[var(--muted)]">Temporary executor access for this checklist item.</p>
        </div>
        <span className="rounded-full border border-[color-mix(in_srgb,var(--warning)_40%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">Burner</span>
      </div>

      {notice ? (
        <p className={`rounded-md border px-2 py-1 text-xs ${notice.tone === 'error' ? 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]' : 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'}`}>
          {notice.text}
        </p>
      ) : null}

      {recordsUnavailable ? (
        <p className="rounded-md border border-[color-mix(in_srgb,var(--warning)_20%,transparent)] bg-[color-mix(in_srgb,var(--warning)_8%,transparent)] px-2 py-1 text-xs text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">
          Existing Field Pass records for this item are unavailable right now. You can still create a fresh pass.
        </p>
      ) : null}

      <div className="grid gap-2">
        {activeAssignmentCards.length === 0 ? (
          <p className="text-xs text-[var(--muted)]">No active Field Pass has this item yet.</p>
        ) : activeAssignmentCards.map((assignment) => (
          <article key={assignment.id} className="rounded-lg border border-[color-mix(in_srgb,var(--warning)_25%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[var(--text-strong)]">{assignment.displayName}</p>
                <p className="mt-0.5 text-[11px] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">Expires {formatExpiry(assignment.expiresAt)}</p>
              </div>
              <Button
                variant="tertiary"
                size="sm"
                disabled={busy === `remove:${assignment.fieldPassId}`}
                onClick={() => void removeAssignment(assignment.fieldPassId)}
              >
                Remove
              </Button>
            </div>
          </article>
        ))}
      </div>

      {activePasses.length > 0 && !hasActiveAssignment ? (
        <div className="grid gap-2">
          <Select
            trigger="labeledPill"
            label="Field Pass"
            ariaLabel="Active Field Pass"
            value={selectedPassId || null}
            placeholder="Choose active Field Pass"
            options={activePasses.map((pass) => ({ value: pass.id, label: passLabel(pass) }))}
            onChange={setSelectedPassId}
          />
          <Button variant="secondary" size="sm" disabled={!selectedPassId || busy === 'add'} onClick={() => void addToExisting()}>
            Add to Field Pass
          </Button>
        </div>
      ) : null}

      <div className="grid gap-2 border-t border-[var(--border)] pt-3">
        <Button variant="primary" size="sm" onClick={() => setDelegateOpen(true)}>
          Create Field Pass
        </Button>
        {lastLink ? (
          <Pressable className="break-all rounded-md border border-[var(--border)] bg-[var(--surface)] p-2 text-left text-[11px] text-[var(--text)]" onClick={() => void copyText(lastLink).then(() => emitNotice('Field Pass link copied.'))}>
            {lastLink}
          </Pressable>
        ) : null}
        <p className="text-[11px] text-[var(--muted)]">Current item: {checklistTitle}</p>
      </div>
      <QuickDelegateSheet
        open={delegateOpen}
        workspaceId={workspaceId}
        targets={[{ id: checklistItemId, title: checklistTitle }]}
        onClose={() => setDelegateOpen(false)}
        onNotice={emitNotice}
        onCreated={async (result, url) => {
          setLastLink(url)
          setPasses((current) => [result.pass, ...current.filter((entry) => entry.id !== result.pass.id)])
          setRecordsUnavailable(false)
          await refresh()
        }}
      />
    </section>
  )
}
